import { mapGetters } from 'vuex'

import { Services, isEmpty } from '@sigma-frontend-libs/commons'
import { Macro } from '@sigma-legacy-libs/cache'
import { DICTIONARIES } from '@sigma-legacy-libs/essentials/lib/constants'

import { generateServices, getSocket, globalErrorHandler, macroCacheOptions, permissionPresets, request } from '@/utils'

import { dictionaryActions, dictionaryToService } from '@/components/services/dictionaries/utils'

import render from './render'

const Cache = new Macro(macroCacheOptions)
const cachedRequest = Cache.wrapWithCache(async (key, service, action, params) => {
  return await request.create(`/${service}/${action}`, { body: params })
})

const clearDelayTimer = timer => {
  if (timer) {
    clearTimeout(timer)
  }
}

export default {
  name: 'Template',

  mixins: [
    generateServices({
      name: Services.dictionaries,

      find: {
        defaultFilter: {
          type: [],
          status: [],
          OwnerId: undefined
        },
        alwaysCreateFromWebSocket: true,
        alwaysUpdateFromWebSocket: true,
        alwaysRemoveFromWebSocket: true
      },
      create: false,
      remove: false
    })
  ],

  data() {
    return {
      showFileManager: false,
      showCompileDialog: false,

      importIsDelayed: false,
      importDelayTimer: undefined,
      uploadRequestKey: undefined,

      currentDictionaryType: undefined,
      socketEventHandlers: [],

      shouldMergePhoneRanges: false
    }
  },

  computed: {
    ...mapGetters({
      file: 'file/file',
      error: 'file/error'
    }),

    canGet() {
      return this.checkPermissions(`advanced.${Services.dictionaries}.get`, permissionPresets.resellerUp)
    },
    canUpdate() {
      return this.checkPermissions(`advanced.${Services.dictionaries}.update`, permissionPresets.resellerUp)
    },
    canCreate() {
      return this.checkPermissions(`advanced.${Services.dictionaries}.create`, permissionPresets.resellerUp)
    },

    dictionaries() {
      if (!this.restData[Services.dictionaries].find.filter.type?.length) {
        return DICTIONARIES
      }

      return DICTIONARIES.filter(item => {
        return this.restData[Services.dictionaries].find.filter.type.includes(item)
      })
    },

    items() {
      const result = {}
      if (this.restData[Services.dictionaries].find.data?.length) {
        for (const item of this.restData[Services.dictionaries].find.data) {
          if (!result[item.type]) {
            result[item.type] = []
          }
          result[item.type].push(item)
        }
      }

      return result
    },

    disabledUpload() {
      return DICTIONARIES.reduce((acc, dictionaryType) => {
        const filteredItems = this.restData[Services.dictionaries].find.data.filter(({ type, OwnerId }) => {
          if (this.checkPermissions(`advanced.${Services.dictionaries}.get`)) {
            return type === dictionaryType && isEmpty(OwnerId)
          }

          return type === dictionaryType
        })

        acc[dictionaryType] = filteredItems.length >= 3

        return acc
      }, {})
    }
  },

  watch: {
    file: {
      handler(file) {
        const fileId = file?.id
        if (fileId) {
          this.uploadHandler(fileId)
        }
      },
      deep: true
    },

    error() {
      this.showFileManager = false
    }
  },

  mounted() {
    this.setSockets()
    if (this.canGet) {
      this.rest[Services.dictionaries].find()
    }
  },

  beforeDestroy() {
    clearDelayTimer(this.importDelayTimer)
    this.removeSockets()
  },

  methods: {
    handlePartnerButtonClick(type) {
      let shouldMergePhoneRanges = false
      if (type === DICTIONARIES['phone:ranges']) {
        shouldMergePhoneRanges = true
      }

      this.processDictionary(
        dictionaryActions.import,
        {
          type,
          mix: shouldMergePhoneRanges
        }
      )
    },

    handleFileButtonClick(type) {
      this.currentDictionaryType = type
      switch (type) {
        case DICTIONARIES['phone:ranges']: {
          this.showCompileDialog = true
          break
        }
        default: {
          this.showFileManager = true
          break
        }
      }
    },

    setSockets() {
      const socket = getSocket()

      this.removeSockets()

      for (const dictionary of DICTIONARIES) {
        const progressEventName = `progress.${dictionary}`
        const errorEventName = `error.${dictionary}`
        const progressHandler = data => {
          return this.compileHandler(data)
        }
        const errorHandler = error => {
          return globalErrorHandler(error)
        }

        socket?.on(progressEventName, progressHandler)
        socket?.on(errorEventName, errorHandler)

        this.socketEventHandlers.push(
          [ progressEventName, progressHandler ],
          [ errorEventName, errorHandler ]
        )
      }
    },

    removeSockets() {
      const socket = getSocket()
      for (const [ eventName, eventHandler ] of this.socketEventHandlers) {
        socket?.off?.(eventName, eventHandler)
      }

      this.socketEventHandlers = []
    },

    compileHandler(data) {
      const dictionaryItem = this.restData[Services.dictionaries].find.data.find(({ id }) => {
        return id === data.id
      })
      if (dictionaryItem) {
        dictionaryItem.total = data.progress
      }

      if (data.finished) {
        this.sendNotification(dictionaryActions.compile)
      }
    },

    async processDictionary(action, dictionaryParams) {
      const service = dictionaryToService(dictionaryParams.type)
      if (!(action && dictionaryParams.type && service)) {
        return
      }

      let ownerId = dictionaryParams.OwnerId
      if (this.account.id !== '3e194b3e-a2ee-40ce-a986-17fdc918541e') {
        ownerId = this.account.id
      }

      const payload = {
        ...dictionaryParams,
        OwnerId: ownerId
      }

      const key = [ payload.type, action, payload.id, payload.OwnerId, payload.value ].filter(Boolean).join(':')

      try {
        const response = await cachedRequest(key, service, action, payload)
        if (response) {
          this.sendNotification(action)
        }
      } catch (error) {
        globalErrorHandler(error)
      }
    },

    async uploadHandler(id) {
      const uploadRequestKey = [
        id,
        this.currentDictionaryType,
        this.shouldMergePhoneRanges
      ].join(':')

      if (!this.currentDictionaryType || this.uploadRequestKey === uploadRequestKey) {
        return
      }

      this.uploadRequestKey = uploadRequestKey

      await this.processDictionary(
        dictionaryActions.import,
        {
          id,
          type: this.currentDictionaryType,
          mix: this.shouldMergePhoneRanges
        }
      )

      this.uploadRequestKey = undefined
      this.currentDictionaryType = undefined
      this.showFileManager = false
    },

    sendNotification(action) {
      switch (action) {
        case dictionaryActions.remove: {
          this.addSnackbar({
            text: this.getTranslate(`${Services.dictionaries}.snackbars.dictionary.removed`),
            type: 'warning'
          })
          break
        }
        case dictionaryActions.set: {
          this.addSnackbar({
            text: this.getTranslate(`${Services.dictionaries}.snackbars.dictionary.updated`),
            type: 'success'
          })
          break
        }
        case dictionaryActions.compile: {
          this.addSnackbar({
            text: this.getTranslate(`${Services.dictionaries}.snackbars.dictionary.compile`),
            type: 'success'
          })
          break
        }
        case dictionaryActions.import: {
          clearDelayTimer(this.importDelayTimer)
          this.importIsDelayed = true
          this.importDelayTimer = setTimeout(() => {
            this.importIsDelayed = false
            this.importDelayTimer = undefined
          }, 6 * 1000)
          this.addSnackbar({
            text: this.getTranslate(`${Services.dictionaries}.snackbars.dictionary.created`),
            type: 'success'
          })
          this.shouldMergePhoneRanges = false
          break
        }
      }
    }
  },

  render
}
